import React from 'react';
import {AnimatePresence, motion} from "framer-motion";
import {useTranslation} from "react-i18next";
import AnimatedCounter from "@/components/AnimatedCounter.jsx";
import {HalfTimeRewardBanner} from "@/features/contest/HalfTimeRewardBanner.jsx";

const FootballRow = ({row, onClick}) => {
    const isMeRow = row.isMe;
    const isTop3 = row.rank <= 3;

    const highlightClass = isMeRow
        ? "bg-green-50 border-action-green shadow-[0_4px_15px_rgba(16,185,129,0.25)] z-20 scale-[1.03] ring-1 ring-action-green"
        : "bg-slate-50 border-transparent hover:border-gray-200 cursor-pointer";

    const fadedClass = row.isFaded ? "opacity-60" : "";
    const rivalClass = row.isRival ? "border-b-2 border-dashed border-gray-300" : "";

    return (
        <motion.div
            layout
            onClick={onClick}
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, scale: 0.9}}
            className={`flex items-center p-3 rounded-xl mb-2 border relative transition-colors ${highlightClass} ${fadedClass} ${rivalClass}`}
        >
            <RankBadge rank={row.rank} isMe={isMeRow}/>
            <div className="flex-1 min-w-0 mr-2 flex items-center gap-1.5">
                <span
                    className={`font-bold truncate ${isMeRow || row.isRival || isTop3 ? 'text-gray-900 text-base' : 'text-gray-500 text-sm'}`}>
                    {row.pseudo}
                </span>
                {row.quizBonus > 0 && (
                    <span title={`+${row.quizBonus} pts quiz`} className="shrink-0 text-action-green text-xs leading-none">⚡</span>
                )}
            </div>
            <div
                className="flex-shrink-0 whitespace-nowrap text-[0.7rem] font-bold text-slate-500 bg-white border border-slate-200 px-2 py-1 rounded mr-3 font-mono shadow-sm">
                {row.betDisplay}
            </div>
            <AnimatedCounter
                value={row.points}
                className={`flex-shrink-0 font-black whitespace-nowrap text-right min-w-[70px] ${isMeRow ? 'z-20 text-action-green text-lg' : (isTop3 ? 'text-gray-900 text-base' : 'text-gray-400 text-sm')}`}
                enableEffect={isMeRow}
                ptsClass={isMeRow ? 'text-sm' : 'text-xs'}
            />
        </motion.div>
    );
};

const RugbyRow = ({row, onClick, t}) => {
    const isMeRow = row.isMe;
    const isTop3 = row.rank <= 3;

    const isDraw = !row.winningTeamName;
    const margin = row.predictionMargin || 0;
    const logoUrl = row.winningTeamLogoUrl;

    const highlightClass = isMeRow
        ? "bg-green-50 border-action-green shadow-[0_4px_15px_rgba(16,185,129,0.25)] z-20 scale-[1.03] ring-1 ring-action-green"
        : "bg-slate-50 border-transparent hover:border-gray-200 cursor-pointer";

    const fadedClass = row.isFaded ? "opacity-60" : "";
    const rivalClass = row.isRival ? "border-b-2 border-dashed border-gray-300" : "";

    return (
        <motion.div
            layout
            onClick={onClick}
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, scale: 0.9}}
            className={`flex items-center p-3 rounded-xl mb-2 border relative transition-colors ${highlightClass} ${fadedClass} ${rivalClass}`}
        >
            <RankBadge rank={row.rank} isMe={isMeRow}/>
            <div className="flex-1 min-w-0 mr-2 flex items-center gap-1.5">
                <span
                    className={`font-bold truncate ${isMeRow || row.isRival || isTop3 ? 'text-gray-900 text-base' : 'text-gray-500 text-sm'}`}>
                    {row.pseudo}
                </span>
                {row.quizBonus > 0 && (
                    <span title={`+${row.quizBonus} pts quiz`} className="shrink-0 text-action-green text-xs leading-none">⚡</span>
                )}
            </div>
            <div className={`
                flex-shrink-0 flex items-center justify-center gap-2
                bg-white border border-slate-200 rounded-lg
                px-2 py-1.5 min-w-[75px] shadow-sm
            `}>
                {isDraw ? (
                    <>
                        <span className="text-sm">🤝</span>
                        <span
                            className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t('leaderboard.draw')}</span>
                    </>
                ) : (
                    <>
                        {logoUrl ? (
                            <img
                                src={logoUrl}
                                alt="Team"
                                className="w-5 h-5 object-contain"
                                onError={(e) => e.target.style.display = 'none'}
                            />
                        ) : (
                            <span className="text-[9px] font-bold text-slate-400 uppercase">
                                {row.winningTeamName?.substring(0, 3)}
                            </span>
                        )}
                        <span className="font-black text-xs text-slate-700">
                            +{margin}
                        </span>
                    </>
                )}
            </div>
            <AnimatedCounter
                value={row.points}
                className={`flex-shrink-0 font-black whitespace-nowrap text-right min-w-[80px] ${isMeRow ? 'z-20 text-action-green text-lg' : (isTop3 ? 'text-gray-900 text-base' : 'text-gray-400 text-sm')}`}
                enableEffect={isMeRow}
                ptsClass={isMeRow ? 'text-sm' : 'text-xs'}
            />
        </motion.div>
    );
};

const RankBadge = ({rank, isMe}) => (
    <div className={`flex-shrink-0 w-[28px] h-[28px] rounded-full flex justify-center items-center text-xs font-bold mr-3 shadow-sm 
        ${isMe ? 'bg-action-green text-white scale-110'
        : (rank === 1 ? 'bg-yellow-100 text-yellow-700'
            : rank === 2 ? 'bg-gray-100 text-gray-600'
                : rank === 3 ? 'bg-orange-100 text-orange-700'
                    : 'bg-slate-200 text-slate-500')}`}
    >
        {rank}
    </div>
);


export const StepLeaderboard = ({
                                    leaderboard,
                                    isGoalFlashing,
                                    setSelectedPlayer = () => {
                                    },
                                    canPlayQuiz = false,
                                    onPlayQuiz = () => {
                                    },
                                    halfTimeReward = null,
                                    onShowHalfTimeReward = () => {
                                    }
                                }) => {

    const {t} = useTranslation();

    const firstRealRow = leaderboard.find(row => !row.isSeparator);
    const isRugbyContest = firstRealRow?.type === 'RUGBY';

    const tieBreakerText = isRugbyContest
        ? t('leaderboard.tie_breaker_precision')
        : t('leaderboard.tie_breaker_speed');

    const tieBreakerIcon = isRugbyContest ? "fa-crosshairs" : "fa-clock";

    return (
        <div className="mt-6 text-center animate-fade-in pb-20">

            {/* Bandeau persistant : lot mi-temps gagné. Cliquable (→ QR) si récupérable,
                sinon badge de rappel (récupéré / expiré). */}
            <HalfTimeRewardBanner halfTimeReward={halfTimeReward} onShow={onShowHalfTimeReward} className="mb-4"/>


            {/* Bandeau persistant : rejouer le quiz à tout moment si refusé */}
            {canPlayQuiz && (
                <motion.button
                    initial={{opacity: 0, y: -10}}
                    animate={{opacity: 1, y: 0}}
                    onClick={onPlayQuiz}
                    className="w-full mb-4 flex items-center justify-between gap-3 bg-brand-dark text-white rounded-2xl px-4 py-3 shadow-[0_8px_24px_-8px_rgba(16,185,129,0.5)] active:scale-[0.98] transition-transform"
                >
                    <span className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-full bg-action-green/20 border border-action-green/30 flex items-center justify-center text-base shrink-0">⚡</span>
                        <span className="text-left">
                            <span className="block text-sm font-black leading-tight">
                                {t('leaderboard.quiz_boost.banner_title', 'Booste ton score')}
                            </span>
                            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                {t('leaderboard.quiz_boost.banner_sub', 'Joue le quiz du jour')}
                            </span>
                        </span>
                    </span>
                    <span className="text-xs font-black text-action-green uppercase tracking-widest shrink-0">
                        {t('leaderboard.quiz_boost.banner_cta', 'Jouer')} →
                    </span>
                </motion.button>
            )}

            <div className="animate-fade-in mb-4">
                {leaderboard.length > 0 && (
                    <div className="flex justify-center mb-3">
                        <p className="flex items-center gap-1.5 text-[9px] text-gray-400 italic font-medium opacity-80">
                            <i className={`fa-regular ${tieBreakerIcon} text-[9px]`}></i>
                            <span>{tieBreakerText}</span>
                        </p>
                    </div>
                )}
                <div
                    className={`bg-white rounded-2xl shadow-soft p-4 text-left relative overflow-hidden transition-all duration-500 ${isGoalFlashing ? 'animate-shuffle' : ''}`}>
                    <AnimatePresence>
                        {leaderboard.map((row) => {
                            if (row.isSeparator) {
                                return (
                                    <motion.div layout key={row.id} initial={{opacity: 0}} animate={{opacity: 1}}
                                                className="text-center text-gray-300 text-xs my-2 tracking-widest">...</motion.div>
                                );
                            }
                            const isRugby = row.type === 'RUGBY';
                            return isRugby ? (
                                <RugbyRow key={row.id} row={row} onClick={() => setSelectedPlayer(row)} t={t}/>
                            ) : (
                                <FootballRow key={row.id} row={row} onClick={() => setSelectedPlayer(row)}/>
                            );
                        })}
                    </AnimatePresence>

                    {leaderboard.length === 0 && (
                        <div className="text-center py-10 text-gray-400 text-sm italic">
                            {t('leaderboard.empty')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};